import { Stack, Text } from "@certn/cascadia";
import {
  ActionBar,
  Card,
  Divider,
  HoverInfo,
  Icon,
  LinkButton,
  TextInputFormikField,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { Formik } from "formik";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { object, string } from "yup";

import { Schemas } from "../../../types";

interface ApplyLinkEmailFormProps {
  submitEmail: (values: { email: string }) => void;
  checks?: Schemas["ApplyLinkDetails"]["checks"];
  credentials?: Schemas["ApplyLinkDetails"]["credentials"];
  customerGroup?: string;
  message?: string;
  isSubmittingToAPI: boolean;
}

export function ApplyLinkEmailForm({
  submitEmail,
  checks,
  credentials,
  customerGroup,
  message,
  isSubmittingToAPI,
}: ApplyLinkEmailFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const schema = object({
    email: string().email().required().label(t("Email address")),
  });

  const hasChecks = checks && checks.length > 0;
  const hasCredentials = credentials && credentials.length > 0;

  return (
    <Formik
      initialValues={{ email: "" }}
      onSubmit={(values) => {
        submitEmail(values);
      }}
      validationSchema={schema}
    >
      {({ handleSubmit }) => {
        return (
          <Stack orientation="vertical" gap="space-6">
            <Stack orientation="vertical" gap="space-5">
              <Text as="h1" style="global/title-small" testID="page-title">
                {t("Background check for {{groupName}}", {
                  groupName: customerGroup,
                })}
              </Text>
              <Text style="form/body-primary">
                {t("This request includes:")}
              </Text>
              {hasChecks && (
                <Stack
                  orientation="vertical"
                  gap="space-2"
                  data-testid="apply-link-check-section"
                  className={styles.credentialsAndChecksPadding}
                >
                  <Stack orientation="vertical" gap="space-2">
                    <Stack orientation="horizontal" gap="space-2">
                      <Icon name="language" color="icon-weak" size="space-6" />
                      <Text color="text-base" style="summary/section-title">
                        {t("In Browser")}
                      </Text>
                    </Stack>
                    {checks?.map((check) => (
                      <Stack
                        orientation="horizontal"
                        gap="space-2"
                        key={check.name}
                      >
                        <Icon
                          name="check"
                          color="icon-accent-tofino"
                          size="space-6"
                        />
                        <Text
                          style="form/body-primary"
                          testID="apply-link-check-name"
                        >
                          {check.name}
                        </Text>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              )}
              {hasCredentials && (
                <Stack
                  orientation="vertical"
                  gap="space-2"
                  className={styles.credentialsAndChecksPadding}
                >
                  <Divider />
                  <Stack
                    orientation="horizontal"
                    gap="space-2"
                    justify="space-between"
                  >
                    <Stack orientation="horizontal" gap="space-2">
                      <Icon
                        name="my-certn-app"
                        color="icon-accent-tofino"
                        type="custom"
                      />
                      <Text color="text-base" style="summary/section-title">
                        {t("MyCertn App")}
                      </Text>
                    </Stack>
                    <HoverInfo
                      trigger={
                        <LinkButton
                          textStyle="form/body-secondary"
                          testID="apply-link-whats-this-button"
                        >
                          {t("What's this?")}
                        </LinkButton>
                      }
                      infoContent={t(
                        "The MyCertn app is the easy and secure way to store and manage all the verified credentials you need, for this and future applications, right on your mobile device."
                      )}
                      testID="apply-link-hover-info"
                    />
                  </Stack>
                  {credentials?.map((credentials) => (
                    <Stack
                      orientation="horizontal"
                      gap="space-2"
                      key={credentials.name}
                    >
                      <Icon
                        name="check"
                        color="icon-accent-tofino"
                        size="space-6"
                      />
                      <Text
                        style="form/body-primary"
                        testID="apply-link-check-name"
                      >
                        {credentials.name}
                      </Text>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Stack>
            {message && (
              <Card
                background="background-accent-neutral-weakest"
                className={styles.additionalMessageCard}
                data-testid="apply-link-note-section"
              >
                <Text style="global/heading-small">
                  {t("A note from {{group}}:", {
                    group: customerGroup,
                  })}
                </Text>
                <Text testID="apply-link-note">{message}</Text>
              </Card>
            )}
            <Stack orientation="vertical" gap="space-2">
              <Text style="global/heading-medium">
                {t("Enter your email to get started")}
              </Text>
              <Text style="form/body-secondary">
                <Trans>
                  If you've already started this background check,{" "}
                  <LinkButton
                    onPress={() => navigate("/")}
                    textStyle="form/body-secondary"
                    linkStyle="monochrome"
                    testID="apply-link-sign-in-button"
                  >
                    sign in
                  </LinkButton>{" "}
                  to continue.
                </Trans>
              </Text>
            </Stack>
            <Stack orientation="vertical" gap="space-2">
              <TextInputFormikField
                name="email"
                label={t("Email address")}
                testID="email-address-input"
                isRequired
              />
              <Text style="input/helper">
                {t(
                  "This email will help us manage your background check and contact you about it."
                )}
              </Text>
            </Stack>
            <ActionBar
              primaryText={t("Next step")}
              onPrimaryClick={() => handleSubmit()}
              isLoading={isSubmittingToAPI}
              testID="apply-link-submit-button"
            />
          </Stack>
        );
      }}
    </Formik>
  );
}

const styles = {
  additionalMessageCard: css({
    padding: "var(--space-3) var(--space-4)",
  }),
  credentialsAndChecksPadding: css({
    paddingLeft: "var(--space-4)",
  }),
};
